import { useCallback } from 'react';
import { Button } from '@cbhq/cds-web/buttons';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { VStack } from '@cbhq/cds-web/layout';
import { PopoverPanel } from '@cbhq/cds-web/overlays';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { defineMessages, useIntl } from '@cbhq/i18n';

const messages = defineMessages({
  exportButton: {
    id: 'Activity.auditExportPopover.exportButton',
    defaultMessage: 'Export',
    description: 'Label for the audit log export popover trigger button',
  },
  popoverAccessibilityLabel: {
    id: 'Activity.auditExportPopover.popoverAccessibilityLabel',
    defaultMessage: 'Export audit logs',
    description: 'Accessibility label for the audit log export popover panel',
  },
  thisViewTitle: {
    id: 'Activity.auditExportPopover.thisViewTitle',
    defaultMessage: 'This view (.csv)',
    description: 'Title for the export current view option',
  },
  last90DaysTitle: {
    id: 'Activity.auditExportPopover.last90DaysTitle',
    defaultMessage: 'Last 90 days (.csv)',
    description: 'Title for the export last 90 days option',
  },
  customReportTitle: {
    id: 'Activity.auditExportPopover.customReportTitle',
    defaultMessage: 'Create custom report',
    description: 'Title for the create custom report option',
  },
  actionCount: {
    id: 'Activity.auditExportPopover.actionCount',
    defaultMessage: '{count, number} {count, plural, one {action} other {actions}}',
    description: 'Description showing the number of audit log actions for an export option',
  },
});

type AuditExportPopoverProps = {
  thisViewActionCount?: number;
  last90DaysActionCount?: number;
  onSelectThisView: () => void;
  onSelectLast90Days: () => void;
  onSelectCustomReport: () => void;
};

type AuditExportPopoverContentProps = AuditExportPopoverProps & {
  closePopover: () => void;
};

function AuditExportPopoverContent({
  closePopover,
  thisViewActionCount,
  last90DaysActionCount,
  onSelectThisView,
  onSelectLast90Days,
  onSelectCustomReport,
}: AuditExportPopoverContentProps) {
  const { formatMessage } = useIntl();

  const handleSelectThisView = useCallback(() => {
    logEvent('activity_export_option_selected', {
      componentType: ComponentType.button,
      action: ActionType.click,
      export_type: 'this_view',
    });
    onSelectThisView();
    closePopover();
  }, [onSelectThisView, closePopover]);

  const handleSelectLast90Days = useCallback(() => {
    logEvent('activity_export_option_selected', {
      componentType: ComponentType.button,
      action: ActionType.click,
      export_type: 'last_90_days',
    });
    onSelectLast90Days();
    closePopover();
  }, [onSelectLast90Days, closePopover]);

  const handleSelectCustomReport = useCallback(() => {
    logEvent('activity_export_option_selected', {
      componentType: ComponentType.button,
      action: ActionType.click,
      export_type: 'custom_report',
    });
    onSelectCustomReport();
    closePopover();
  }, [onSelectCustomReport, closePopover]);

  const thisViewDescription =
    thisViewActionCount != null
      ? formatMessage(messages.actionCount, { count: thisViewActionCount })
      : undefined;

  const last90DaysDescription =
    last90DaysActionCount != null
      ? formatMessage(messages.actionCount, { count: last90DaysActionCount })
      : undefined;

  return (
    <VStack gap={0} width="100%" paddingY={0.5}>
      <ListCell
        spacingVariant="condensed"
        title={formatMessage(messages.thisViewTitle)}
        description={thisViewDescription}
        onClick={handleSelectThisView}
      />
      <ListCell
        spacingVariant="condensed"
        title={formatMessage(messages.last90DaysTitle)}
        description={last90DaysDescription}
        onClick={handleSelectLast90Days}
      />
      <ListCell
        spacingVariant="condensed"
        title={formatMessage(messages.customReportTitle)}
        onClick={handleSelectCustomReport}
      />
    </VStack>
  );
}

const PANEL_WIDTH = 220;
const CONTENT_POSITION = { placement: 'bottom-end', gap: 1 } as const;

export function AuditExportPopover({
  thisViewActionCount,
  last90DaysActionCount,
  onSelectThisView,
  onSelectLast90Days,
  onSelectCustomReport,
}: AuditExportPopoverProps) {
  const { formatMessage } = useIntl();

  const handleOpenPopover = useCallback(() => {
    logEvent('activity_export_popover_opened', {
      componentType: ComponentType.button,
      action: ActionType.click,
    });
  }, []);

  const renderContent = useCallback(
    ({ closePopover }: { closePopover: () => void }) => (
      <AuditExportPopoverContent
        closePopover={closePopover}
        thisViewActionCount={thisViewActionCount}
        last90DaysActionCount={last90DaysActionCount}
        onSelectThisView={onSelectThisView}
        onSelectLast90Days={onSelectLast90Days}
        onSelectCustomReport={onSelectCustomReport}
      />
    ),
    [
      last90DaysActionCount,
      onSelectCustomReport,
      onSelectLast90Days,
      onSelectThisView,
      thisViewActionCount,
    ],
  );

  return (
    <PopoverPanel
      panelWidth={PANEL_WIDTH}
      accessibilityLabel={formatMessage(messages.popoverAccessibilityLabel)}
      contentPosition={CONTENT_POSITION}
      content={renderContent}
      onOpen={handleOpenPopover}
    >
      <Button accessibilityLabel={formatMessage(messages.popoverAccessibilityLabel)} compact>
        {formatMessage(messages.exportButton)}
      </Button>
    </PopoverPanel>
  );
}
